import {Alert, Button, Checkbox, Form, Input, Row, Typography} from 'antd';
import {FC, useContext, useState} from 'react';
import {Redirect, useParams} from 'react-router-dom';
import {usePutSession, useResetUserPassword} from '../api';
import {getApiExceptionData, isApiException} from '../api-helpers';
import {SessionContext} from './Session';
import {wakHandleRequest, wakLogin, wakLogout} from '../api-wak';

const {Title} = Typography;

const {
  REACT_APP_DEFAULT_USERNAME,
  REACT_APP_DEFAULT_PASSWORD,
  REACT_APP_OFFLINE_MODE,
} = process.env;

const printableErrors: {[key: string]: string} = {
  'user not found': "L'utilisateur n'existe pas",
  'user password is invalid': 'Le mot de passe est invalide',
  'Failed to fetch: Failed to fetch': 'Impossible de se connecter au serveur',
};

export const getErrorString = (error: any) => {
  let message = '';
  if (isApiException(error)) {
    const data = getApiExceptionData(error);
    message = data.message;
  } else {
    message = error.message;
  }
  return printableErrors[message] ?? message;
};

const layout = {};
const tailLayout = {
  wrapperCol: {span: 16},
};

interface LoginFormValues {
  username: string;
  password: string;
  remember: boolean;
}

interface LoginParams {
  authToken?: string;
}

/** Special "indu@earow.fr" user ID */
export const INDUS_CLIENT_ID = '4F45D1EAFF84484C8485BDB9DC5EB1E1';

const Login: FC = () => {
  let {authToken} = useParams<LoginParams>();

  const [error, setError] = useState<string>();
  const [success, setSuccess] = useState<string>();
  const [session, setSession] = useContext(SessionContext);
  const [loadingLogin, setLoadingLogin] = useState(false);
  const {
    mutate: resetPassword,
    loading: loadingResetPassword,
  } = useResetUserPassword({});
  const [resetMode, setResetMode] = useState(false);
  const [attemptedTokenLogin, setAttemptedTokenLogin] = useState(false);

  const onFinish = async ({username, password, remember}: LoginFormValues) => {
    try {
      if (resetMode) {
        await resetPassword({login: username});
        setSuccess(
          'Si votre compte existe, votre mot de passe a été réinitialisé et vous a été communiqué par email.',
        );
        setResetMode(false);
      } else {
        setLoadingLogin(true);

        let res: Response;
        res = await wakLogin(username, password);
        if (!res.ok) {
          if (res.status === 401) {
            throw new Error('Les identifiants sont incorrects');
          } else {
            throw new Error('Impossible de se connecter au serveur');
          }
        }
        const resUser = await wakHandleRequest('Utils', 'getUser');
        const user = await resUser.json();

        if (user.result.ID === INDUS_CLIENT_ID) {
          await wakLogout();
          throw new Error('Les identifiants sont incorrects');
        }

        setSession({
          token: '1', // deprecated
          expires: '3600', // deprecated
          remember: true, // deprecated
          user: {
            raisonSociale: user.result.fullName,
            identifiant: user.result.identifiant,
            code: user.result.code,
            id_intersonProtac: user.result.id_intersonProtac,
            // id_surdifuse: user.result.id_surdifuse,
            id_emboutFrancais: user.result.id_emboutFrancais,
            id_accessoire: user.result.id_accessoire,

            ID: user.result.ID,
            sommeil: user.result.sommeil,
          },
        });
        setLoadingLogin(false);
      }
    } catch (error) {
      setLoadingLogin(false);
      setError(getErrorString(error));
      setSuccess('');
    }
  };

  if (authToken && !attemptedTokenLogin) {
    setAttemptedTokenLogin(true);
    console.log('attempting token login', {
      attemptedTokenLogin,
      authToken,
    });

    (async () => {
      const [username, password] = [
        authToken.substr(0, 40),
        authToken.substr(40, 40),
      ];
      onFinish({username, password, remember: true});
    })();
  }

  if (session?.token !== undefined) {
    const redirect = new URLSearchParams(
      window.location.search.replace(/^\?/, ''),
    ).get('redirect');

    return <Redirect to={redirect ?? '/'} />;
  }

  return (
    <div className="login-container">
      {error ? (
        <Alert
          message="Erreur de connexion"
          description={error}
          type="error"
          closable
          style={{marginBottom: 20, width: '100%'}}
          onClose={() => setError('')}
        />
      ) : null}
      {success ? (
        <Alert
          message="Succès"
          description={success}
          type="success"
          closable
          style={{marginBottom: 20, width: '100%'}}
          onClose={() => setSuccess('')}
        />
      ) : null}

      <div className="form-container">
        <div className="background-container">
          <img
            src="/assets/2022/connexion(1).jpg"
            alt="Fond connexion"
            style={{maxWidth: '1200px'}}
          />
        </div>

        <Row>
          <Form<LoginFormValues>
            {...layout}
            name="basic"
            className="login-form"
            initialValues={{remember: true}}
            onFinish={onFinish}
            style={{zIndex: 1}}
          >
            <Title style={{textTransform: 'uppercase'}} level={1}>
              Connexion
            </Title>
            <Title
              style={{textTransform: 'uppercase', paddingLeft: '5px'}}
              level={2}
            >
              À votre Espace Pro
            </Title>

            <div style={{marginTop: '30px', maxWidth: 'calc(100vw - 20px)'}}>
              <Form.Item
                label="Identifiant"
                name="username"
                initialValue={
                  REACT_APP_DEFAULT_USERNAME ??
                  (REACT_APP_OFFLINE_MODE ? 'offline@example.com' : '')
                }
                rules={[
                  {
                    required: true,
                    message: 'Veuillez entrer votre identifiant',
                  },
                ]}
              >
                <Input />
              </Form.Item>

              {resetMode ? null : (
                <>
                  <Form.Item
                    label="Mot de passe"
                    name="password"
                    initialValue={
                      REACT_APP_DEFAULT_PASSWORD ??
                      (REACT_APP_OFFLINE_MODE ? 'password' : '')
                    }
                    rules={[
                      {
                        required: true,
                        message: 'Veuillez entrer votre mot de passe',
                      },
                    ]}
                  >
                    <Input.Password />
                  </Form.Item>
                  <Form.Item
                    {...tailLayout}
                    name="remember"
                    valuePropName="checked"
                  >
                    <Checkbox>Se souvenir de moi</Checkbox>
                  </Form.Item>
                </>
              )}

              <Form.Item {...tailLayout} style={{textAlign: 'left'}}>
                <div>
                  <Button
                    type="primary"
                    htmlType="submit"
                    loading={loadingLogin || loadingResetPassword}
                    style={{textTransform: 'uppercase'}}
                  >
                    {resetMode ? 'Réinit. mot de passe' : 'Se connecter'}
                  </Button>
                </div>
                <div>
                  <Button
                    style={{marginTop: '5px', padding: '0'}}
                    htmlType="button"
                    onClick={() => setResetMode(!resetMode)}
                    type="link"
                  >
                    {resetMode ? 'Se connecter' : 'Mot de passe oublié ?'}
                  </Button>
                </div>
              </Form.Item>
            </div>
          </Form>
        </Row>
      </div>
    </div>
  );
};

export default Login;
